/**
 * Affiliate attribution, browser side. THE FALLBACK PATH ONLY.
 *
 * The real mechanism is a server-set httpOnly cookie, written by the engine on every click
 * through /api/affiliates/r/:code and read by the engine at checkout. The portal is not
 * involved in that at all, which is deliberate: a cookie written from JavaScript is capped
 * at seven days by Safari's ITP, while one delivered in a Set-Cookie header from a
 * first-party origin is not. Doing this in the SPA would quietly turn a 30 day window into
 * a 7 day one for a large slice of traffic, with nothing failing.
 *
 * SO WHY DOES THIS EXIST. Cookies can be refused outright: a hardened privacy browser, an
 * embedded webview, a user who blocks them. Those buyers would attribute to nobody. The
 * redirect also carries ?vyrc= through to the destination, so the page can record it here
 * and send it at checkout as a second-best signal.
 *
 * IT IS SECOND BEST, AND THE ENGINE TREATS IT THAT WAY. Where both exist the cookie wins,
 * because the cookie is rewritten on every click and this is only whatever URL the page
 * happened to load with. That ordering is what keeps last-click honest.
 *
 * The 30 days here is a mirror of the engine's window, checked locally so a stale value
 * from months ago is not sent at all.
 */

const KEY = 'voleto.affiliate';
const PARAM = 'vyrc';
const WINDOW_MS = 30 * 24 * 60 * 60 * 1000;

interface Stored { code: string; at: number }

function read(): Stored | null {
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Stored;
    if (typeof parsed?.code !== 'string' || typeof parsed?.at !== 'number') return null;
    return parsed;
  } catch {
    // Storage can throw outright, not just return null: Safari private mode and any
    // context with site data disabled. A dead fallback must not break the page it is on.
    return null;
  }
}

/**
 * Record a code seen in the URL. Called once on load.
 *
 * LAST CLICK, AND THE WINDOW RE-ARMS: this overwrites unconditionally and stamps a fresh
 * timestamp, matching what the cookie does server-side. A different affiliate's code
 * replaces the previous one; the same code extends it.
 */
export function captureAffiliateFromUrl(search: string = window.location.search): void {
  try {
    const code = new URLSearchParams(search).get(PARAM);
    if (!code || !code.trim()) return;
    window.localStorage.setItem(KEY, JSON.stringify({
      code: code.trim().slice(0, 64), at: Date.now(),
    }));
  } catch { /* see read() */ }
}

/** The code to send at checkout, or null when there is none or it has aged out. */
export function currentAffiliateCode(): string | null {
  const stored = read();
  if (!stored) return null;
  if (Date.now() - stored.at > WINDOW_MS) return null;
  return stored.code;
}
